import { Routes, Route, Navigate } from 'react-router-dom';
import { UserRole } from '../shared/types.js';
import Layout from './components/Layout.js';
import ProtectedRoute from './components/ProtectedRoute.js';
import LoginPage from './pages/LoginPage.js';
import AuthVerifyPage from './pages/AuthVerifyPage.js';
import ScanPage from './pages/ScanPage.js';
import ScanStatusPage from './pages/ScanStatusPage.js';
import SharedScoreCardPage from './pages/SharedScoreCardPage.js';
import ArchivePage from './pages/ArchivePage.js';
import ConfigLayout from './pages/config/ConfigLayout.js';
import CategoriesPage from './pages/config/CategoriesPage.js';
import ChecksPage from './pages/config/ChecksPage.js';
import QuickWinsPage from './pages/config/QuickWinsPage.js';
import DisclaimerPage from './pages/config/DisclaimerPage.js';
import TemplatesPage from './pages/config/TemplatesPage.js';
import CheckPacksPage from './pages/config/CheckPacksPage.js';
import ConfigPlusLayout from './pages/config-plus/ConfigPlusLayout.js';
import ConfigPlusTemplatesPage from './pages/config-plus/TemplatesPage.js';
import ScoringEnginePage from './pages/config-plus/ScoringEnginePage.js';
import ConfigPlusCategoriesPage from './pages/config-plus/CategoriesPage.js';
import CheckLibraryPage from './pages/config-plus/CheckLibraryPage.js';
import AliasRegistryPage from './pages/config-plus/AliasRegistryPage.js';
import AdminLayout from './pages/admin/AdminLayout.js';
import UsersPage from './pages/admin/UsersPage.js';
import AuditLogPage from './pages/admin/AuditLogPage.js';
import SettingsPage from './pages/admin/SettingsPage.js';
import GeneralSettingsPage from './pages/admin/settings/GeneralSettingsPage.js';
import SlackSettingsPage from './pages/admin/settings/SlackSettingsPage.js';
import KeySettingsPage from './pages/admin/settings/KeySettingsPage.js';
import WatchdogSettingsPage from './pages/admin/settings/WatchdogSettingsPage.js';
import CronJobsSettingsPage from './pages/admin/settings/CronJobsSettingsPage.js';
import WatchdogScoringDashboardPage from './pages/watchdog/DashboardPage.js';
import WatchdogGtmAnalystPage from './pages/watchdog/GtmAnalystPage.js';
import WatchdogDomainsPage from './pages/watchdog/DomainsPage.js';
import WatchdogGraphPage from './pages/watchdog/GraphPage.js';
import WatchdogJobManagerPage from './pages/watchdog/JobManagerPage.js';
import WatchdogNotificationCenterPage from './pages/watchdog/NotificationCenterPage.js';
import WatchdogNotificationDetailPage from './pages/watchdog/NotificationDetailPage.js';

export default function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/verify" element={<AuthVerifyPage />} />
      <Route path="/share/:token" element={<SharedScoreCardPage />} />

      {/* Authenticated routes */}
      <Route element={<ProtectedRoute />}>
        <Route element={<Layout />}>
          {/* Scan flow - all authenticated users */}
          <Route path="/scan" element={<ScanPage />} />
          <Route path="/scan/:id" element={<ScanStatusPage />} />
          <Route path="/archive" element={<ArchivePage />} />

          {/* Config - diga + admin */}
          <Route element={<ProtectedRoute requiredRoles={[UserRole.DIGA, UserRole.ADMIN]} />}>
            <Route path="/config" element={<ConfigLayout />}>
              <Route index element={<Navigate to="templates" replace />} />
              <Route path="templates" element={<TemplatesPage />} />
              <Route path="categories" element={<CategoriesPage />} />
              <Route path="checks" element={<ChecksPage />} />
              <Route path="check-packs" element={<CheckPacksPage />} />
              <Route path="quick-wins" element={<QuickWinsPage />} />
              <Route path="disclaimer" element={<DisclaimerPage />} />
            </Route>
          </Route>

          {/* Konfiguration+ - diga + admin */}
          <Route element={<ProtectedRoute requiredRoles={[UserRole.DIGA, UserRole.ADMIN]} />}>
            <Route path="/config-plus" element={<ConfigPlusLayout />}>
              <Route index element={<Navigate to="templates" replace />} />
              <Route path="templates" element={<ConfigPlusTemplatesPage />} />
              <Route path="categories" element={<ConfigPlusCategoriesPage />} />
              <Route path="scoring-engine" element={<ScoringEnginePage />} />
              <Route path="check-library" element={<CheckLibraryPage />} />
              <Route path="alias-registry" element={<AliasRegistryPage />} />
            </Route>
          </Route>

          {/* Watchdog - admin only */}
          <Route element={<ProtectedRoute requiredRoles={[UserRole.ADMIN]} />}>
            {/*
              * Die Dashboard-Seite ist am 21.08.2026 zurueckgekommen — jetzt als
              * SCORING DASHBOARD (PRD 21.08.): Auswertung der Scoringsets aus
              * BigQuery. Config bleibt weiterhin draussen, die Datei liegt
              * unveraendert unter pages/watchdog/.
              */}
            <Route path="/watchdog" element={<Navigate to="/watchdog/dashboard" replace />} />
            <Route path="/watchdog/dashboard" element={<WatchdogScoringDashboardPage />} />
            <Route path="/watchdog/gtm-analyst" element={<WatchdogGtmAnalystPage />} />
            <Route path="/watchdog/domains" element={<WatchdogDomainsPage />} />
            <Route path="/watchdog/graph" element={<WatchdogGraphPage />} />
            <Route path="/watchdog/jobs" element={<WatchdogJobManagerPage />} />
            <Route path="/watchdog/notifications" element={<WatchdogNotificationCenterPage />} />
            {/* Ziel des Slack-Deep-Links aus notificationDeepLink() */}
            <Route path="/watchdog/notifications/:id" element={<WatchdogNotificationDetailPage />} />
          </Route>

          {/* Admin - admin only */}
          <Route element={<ProtectedRoute requiredRoles={[UserRole.ADMIN]} />}>
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Navigate to="users" replace />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="audit-log" element={<AuditLogPage />} />
              <Route path="settings" element={<SettingsPage />}>
                <Route index element={<Navigate to="general" replace />} />
                <Route path="general" element={<GeneralSettingsPage />} />
                <Route path="slack" element={<SlackSettingsPage />} />
                <Route path="keys" element={<KeySettingsPage />} />
                <Route path="watchdog" element={<WatchdogSettingsPage />} />
                <Route path="cron-jobs" element={<CronJobsSettingsPage />} />
              </Route>
            </Route>
          </Route>

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/scan" replace />} />
        </Route>
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/scan" replace />} />
    </Routes>
  );
}
